import { normalizePathname } from '@/lib/utils/normalize-pathname'
import type { PageType } from '@/types/nav-types'

/**
 * Routes that open with a full-bleed dark hero running behind the topbar. The
 * nav starts transparent over them and turns navy on scroll (see
 * `useScrollAdjust`), and every such page pairs this with `-mt-20 pt-20` on its
 * header so the dark surface actually extends under the bar.
 *
 * Kept as one list rather than a growing `||` chain — a dark hero is a design
 * decision a page opts into, and this is where that opt-in lives.
 */
const DARK_NAV_ROUTES = [
  /^\/hearing-guide$/,
  /^\/hearing-aids$/,
  /^\/hearing-aids-dashboard$/,
  /^\/authors(?:\/page\/[1-9]\d*|\/[^/]+(?:\/page\/[1-9]\d*)?)?$/,
  /^\/newsletter$/,
  /^\/style-guide\/hearing-aids-hub$/,
  // Public clinic listing and the owner's draft of one: both open with the
  // listing hero, whose navy stage runs up behind the topbar.
  /^\/clinics\/[^/]+$/,
  /^\/onboarding\/drafts\/[^/]+$/,
]

/**
 * Page types whose pages open with the dark hero regardless of pathname. The
 * brand hub can't be a route pattern above: its public URL is the story slug,
 * shared with the segment-a article layout — only segment b (and drafts)
 * classifies as `brand` and renders the navy band, so the page TYPE is the
 * discriminator the pathname can't be.
 */
const DARK_NAV_PAGE_TYPES = new Set<PageType>(['brand'])

/** Whether the current page type is a dark-hero surface (see set above). */
export const isDarkNavPageType = (pageType: PageType): boolean => DARK_NAV_PAGE_TYPES.has(pageType)

/**
 * Whether `pathname` (as returned by `usePathname()`) is a dark-hero route.
 *
 * Normalizing first is load-bearing: at prerender `usePathname()` is the
 * internal `/en/a/authors` route, at request time and on the hydrated client
 * it's the public `/authors`. Matching the raw value made every prerendered
 * dark page ship the LIGHT nav — cream bar, dark logo, dark icons — and flip to
 * the dark treatment on hydration, a cream flash over the navy hero.
 */
export const isDarkNavPath = (pathname: string): boolean => {
  const path = normalizePathname(pathname)
  return DARK_NAV_ROUTES.some((route) => route.test(path))
}
